import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../apiAdmin";

// Admin "Countries" tab — read-only board over GET /admin/countries.
//
// One row per market. Sign-ups are AUTOMATIC — there are no switches here:
// a country derives ✅ Open the moment it has a working payout rail and
// 🔒 Locked when it doesn't (derivation lives server-side in
// lib/countryPolicy.js, the same check every signup door runs, so this board
// can never disagree with actual signup behavior). Layout mirrors the
// founder-approved mockup (.claude/countries-tab-mockup.html).

interface CountryRow {
  code: string;
  name: string;
  flag: string;
  eurozone: boolean;
  stripeSupported: boolean;
  platformCanPayout: boolean;
  settlementCurrency: string | null;
  listed: boolean;
  notes: string;
  open: boolean;
  status: "live" | "payin_fix" | "trapped" | "stripe_blocked" | "unsupported";
  defaultCurrency: string; // any live pay-in currency (Phase C)
  waitlistCount: number;
  // PROJECT ATLAS — prospective MassPay coverage facts (server-computed from
  // the vendor sheets; absent on older servers, so always optional-chain).
  atlas?: {
    masspayWallets: string[];
    masspayFxPublished: boolean;
    cryptoOnly: boolean;
  };
}

// One "Beyond the board" row: a MassPay wallet country not on the Stripe board.
interface AtlasPipelineRow {
  code: string;
  name: string;
  currency: string;
  wallets: string[];
  fxPublished: boolean;
  fxPct: number | null;
  cryptoOnly: boolean;
}

interface CountriesData {
  summary: {
    openCount: number;
    listedCount: number;
    payinCurrencies: string[];
    payoutCurrencies: string[];
    waitlistTotal: number;
    source: "db" | "fallback";
    atlas?: {
      masspayEnabled: boolean;
      walletCountriesTotal: number;
      pipelineCount: number;
      pipelineFxPublished: number;
      pipelineCryptoOnly: number;
    };
  };
  countries: CountryRow[];
  atlasPipeline?: AtlasPipelineRow[];
}

const CHIP_STYLES: Record<CountryRow["status"], { label: string; color: string; bg: string }> = {
  live: { label: "LIVE", color: "var(--adm-green)", bg: "rgba(36,201,97,.12)" },
  payin_fix: { label: "PAY-IN FIX", color: "#b45309", bg: "rgba(255,209,102,.18)" },
  trapped: { label: "TRAPPED $", color: "var(--adm-red)", bg: "rgba(248,81,73,.12)" },
  stripe_blocked: { label: "STRIPE BLOCKS", color: "var(--adm-text-sec)", bg: "rgba(139,148,158,.14)" },
  unsupported: { label: "NOT ON STRIPE", color: "var(--adm-orange)", bg: "rgba(255,166,87,.14)" },
};

function StatusChip({ status }: { status: CountryRow["status"] }) {
  const s = CHIP_STYLES[status];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: 6,
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  );
}

function SignupCell({ open }: { open: boolean }) {
  return (
    <span style={{ fontWeight: 700, whiteSpace: "nowrap", color: open ? "var(--adm-green)" : "var(--adm-text-sec)" }}>
      {open ? "✅ Open" : "🔒 Locked"}
    </span>
  );
}

function CurrencyPill({ code }: { code: string }) {
  return (
    <span
      className="adm-fs-74 adm-bold adm-mb-4 adm-green" style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        marginRight: 6,
        background: "rgba(36,201,97,.12)",
        border: "1px solid rgba(36,201,97,.35)",
      }}
    >
      {code.toUpperCase()}
    </span>
  );
}

// Pay-in / payout cells per the mockup: open rows show real rails; a
// payin_fix row shows "USD → GBP" (fans charged USD today, bank gets GBP).
function payinLabel(c: CountryRow): React.ReactNode {
  if (!c.open) return c.status === "trapped" ? "USD" : "—";
  if (c.status === "payin_fix" && c.settlementCurrency) {
    return (
      <>
        USD → <b>{c.settlementCurrency.toUpperCase()}</b>
      </>
    );
  }
  return c.defaultCurrency.toUpperCase();
}

function payoutLabel(c: CountryRow): string {
  if (c.status === "unsupported" || c.status === "stripe_blocked") return "—";
  return c.settlementCurrency ? c.settlementCurrency.toUpperCase() : "—";
}

function providerLabel(c: CountryRow): string {
  if (c.open) return "🏦 Stripe Connect";
  if (c.status === "unsupported") return "not a Stripe country";
  if (c.status === "trapped") return c.notes.includes("Phase B") ? "🏦 Phase B unlock" : "none working";
  return "—";
}

function CountryTable({ rows, collapseEurozone }: { rows: CountryRow[]; collapseEurozone?: boolean }) {
  const eurozoneRows = collapseEurozone ? rows.filter((r) => r.eurozone) : [];
  const flatRows = collapseEurozone ? rows.filter((r) => !r.eurozone) : rows;

  const renderRow = (c: CountryRow) => (
    <tr key={c.code}>
      <td className="adm-bold adm-nowrap">
        <span className="adm-mr-8">{c.flag}</span>
        {c.name}
      </td>
      <td><SignupCell open={c.open} /></td>
      <td className="adm-nowrap adm-semibold">{payinLabel(c)}</td>
      <td className="adm-nowrap adm-semibold">{payoutLabel(c)}</td>
      <td className="muted-text adm-nowrap adm-fs-78">
        {providerLabel(c)}
        {!c.open && (c.atlas?.masspayWallets?.length ?? 0) > 0 && (
          <span className="adm-block adm-fs-70 adm-bold" style={{ color: "var(--adm-blue)" }}>
            📱 MassPay {c.atlas!.cryptoOnly ? "(crypto-only)" : "wallets"} — Atlas
          </span>
        )}
      </td>
      <td><StatusChip status={c.status} /></td>
      <td className="muted-text adm-fs-78">
        {c.waitlistCount > 0 && (
          <span className="adm-bold" style={{ color: "var(--adm-blue)", marginRight: 6 }}>
            {c.waitlistCount} waiting
          </span>
        )}
        {c.notes || (c.waitlistCount > 0 ? "" : "—")}
      </td>
    </tr>
  );

  return (
    <table className="adm-table-light adm-w-full">
      <thead>
        <tr>
          <th style={{ width: "20%" }}>Market</th>
          <th>Sign-ups</th>
          <th>Pay-in</th>
          <th>Payout</th>
          <th>Provider</th>
          <th>Status</th>
          <th style={{ width: "32%" }}>Waitlist / notes</th>
        </tr>
      </thead>
      <tbody>
        {flatRows.map(renderRow)}
        {eurozoneRows.length > 0 && (
          <tr key="eurozone">
            <td className="adm-bold">
              <details>
                <summary className="adm-pointer adm-nowrap" style={{ listStyle: "none" }}>
                  <span className="adm-mr-8">🇪🇺</span>Eurozone
                  <span className="adm-ml-8 adm-fs-72 adm-semibold" style={{ color: "var(--adm-blue)" }}>
                    {eurozoneRows.length} countries — click to expand
                  </span>
                </summary>
                <div className="muted-text adm-fs-78 adm-mt-8" style={{ fontWeight: 400, lineHeight: 1.9, maxWidth: 340, whiteSpace: "normal" }}>
                  {eurozoneRows.map((c) => `${c.flag} ${c.name}`).join(" · ")}
                </div>
              </details>
            </td>
            <td><SignupCell open /></td>
            <td className="adm-semibold">EUR</td>
            <td className="adm-semibold">EUR</td>
            <td className="muted-text adm-nowrap adm-fs-78">🏦 Stripe Connect</td>
            <td><StatusChip status="live" /></td>
            <td className="muted-text adm-fs-78">
              {(() => {
                const waiting = eurozoneRows.reduce((n, c) => n + c.waitlistCount, 0);
                const noted = eurozoneRows.filter((c) => c.notes);
                return (
                  <>
                    {waiting > 0 && (
                      <span className="adm-bold" style={{ color: "var(--adm-blue)", marginRight: 6 }}>{waiting} waiting</span>
                    )}
                    {noted.length > 0 ? noted.map((c) => `${c.name}: ${c.notes}`).join(" ") : waiting > 0 ? "" : "—"}
                  </>
                );
              })()}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Group({ title, count, hint, children }: { title: string; count: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="adm-card adm-p-16 adm-mb-14">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: "1rem" }}>{title}</h3>
        <span className="muted-text adm-fs-78 adm-semibold">{count}</span>
        {hint && <span className="muted-text adm-fs-78">{hint}</span>}
      </div>
      <div className="adm-scroll-x">{children}</div>
    </div>
  );
}

export default function AdminCountriesTab() {
  const [data, setData] = useState<CountriesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminApi
      .get("/countries")
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't load the countries board. Try refreshing."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const groups = useMemo(() => {
    const rows = data?.countries || [];
    return {
      live: rows.filter((c) => c.status === "live"),
      payinFix: rows.filter((c) => c.status === "payin_fix"),
      trapped: rows.filter((c) => c.status === "trapped"),
      unsupported: rows.filter((c) => c.status === "unsupported"),
      blocked: rows.filter((c) => c.status === "stripe_blocked"),
    };
  }, [data]);

  if (loading) return <p className="muted-text adm-p-12">Loading countries…</p>;
  if (error || !data) {
    return (
      <div className="adm-p-12">
        <p className="muted-text">{error || "No data."}</p>
        <button className="adm-select adm-pointer" onClick={load}>Retry</button>
      </div>
    );
  }

  const { summary } = data;

  return (
    <div>
      <p className="muted-text adm-fs-82" style={{ maxWidth: 760, lineHeight: 1.5 }}>
        One row per market. Sign-ups are <b>automatic</b> — no switches to flip: a country is{" "}
        <b>✅ Open</b> the moment it has a working payout rail and <b>🔒 Locked</b> when it doesn't.
        Locked countries show DJs an honest “join the waitlist” at the start of sign-up.
        Existing DJs are never affected by a lock.
      </p>

      {summary.source === "fallback" && (
        <div className="adm-card adm-mb-12" style={{ padding: 10, borderLeft: "3px solid var(--adm-orange)" }}>
          <span className="adm-fs-80">
            ⚠️ Showing the built-in fallback table — the database copy isn't reachable right now.
          </span>
        </div>
      )}

      {/* Summary cards — all auto-calculated from the same derivation the signup gates use. */}
      <div className="adm-mb-14" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <div className="adm-card adm-accent-green">
          <div className="muted-text adm-eyebrow">
            Countries open for sign-up
          </div>
          <div className="adm-big-num">
            <span className="adm-green">{summary.openCount}</span>
            <span className="muted-text"> / {summary.listedCount}</span>
          </div>
          <div className="muted-text adm-fs-74 adm-mt-4">
            {summary.listedCount - summary.openCount} locked until a payout rail exists.
          </div>
        </div>
        <div className="adm-card adm-accent-green">
          <div className="muted-text adm-eyebrow">
            Fans can pay in (live now)
          </div>
          <div>{summary.payinCurrencies.map((c) => <CurrencyPill key={c} code={c} />)}</div>
        </div>
        <div className="adm-card adm-accent-green">
          <div className="muted-text adm-eyebrow">
            DJs can be paid in (live now)
          </div>
          <div>{summary.payoutCurrencies.map((c) => <CurrencyPill key={c} code={c} />)}</div>
        </div>
        <div className="adm-card adm-p-14">
          <div className="muted-text adm-eyebrow">
            Waitlist
          </div>
          <div className="adm-big-num">{summary.waitlistTotal}</div>
          <div className="muted-text adm-fs-74 adm-mt-4">
            DJs waiting in locked countries — the launch list when a rail goes live.
          </div>
        </div>
        {summary.atlas && (
          <div className="adm-card adm-p-14" style={{ borderLeft: "3px solid var(--adm-blue)" }}>
            <div className="muted-text adm-eyebrow">
              🌍 Project Atlas — MassPay pipeline
            </div>
            <div className="adm-big-num">
              <span style={{ color: "var(--adm-blue)" }}>+{summary.atlas.pipelineCount}</span>
              <span className="muted-text" style={{ fontSize: "0.9rem" }}> new countries</span>
            </div>
            <div className="muted-text adm-fs-74 adm-mt-4">
              {summary.atlas.pipelineFxPublished} full-quote · {summary.atlas.pipelineCryptoOnly} crypto-only ·{" "}
              integration {summary.atlas.masspayEnabled ? "LIVE" : "not signed — nothing open yet"}
            </div>
          </div>
        )}
      </div>

      <Group title="✅ Open — working end to end" count={`${groups.live.length} countries · 2 rows`}>
        <CountryTable rows={groups.live} collapseEurozone />
      </Group>

      <Group
        title="🟡 Open — payout works, fans pay in USD"
        count={`${groups.payinFix.length} countries`}
        hint="— fans pay in USD for now; Phase C lets them pay the DJ's local currency"
      >
        <CountryTable rows={groups.payinFix} />
      </Group>

      <Group
        title="🔴 Locks itself — trapped-money risk today"
        count={`${groups.trapped.length} countries`}
        hint="— sign-up would work but cashout would refuse forever, so these auto-lock. 9 unlock in Phase B on currencies Stripe already supports."
      >
        <CountryTable rows={groups.trapped} />
      </Group>

      <Group title="🟠 Not a Stripe country" count={`${groups.unsupported.length} country`} hint="— hidden from the sign-up picker entirely">
        <CountryTable rows={groups.unsupported} />
      </Group>

      <Group
        title="⚫ Locks itself — Stripe blocks these for a US platform"
        count={`${groups.blocked.length} countries`}
        hint="— onboarding used to die here with a misleading 'temporarily unavailable' error; now it's an honest waitlist"
      >
        <CountryTable rows={groups.blocked} />
      </Group>

      {(data.atlasPipeline?.length ?? 0) > 0 && (
        <Group
          title="🌍 Beyond the board — Project Atlas (MassPay pipeline)"
          count={`${data.atlasPipeline!.length} countries`}
          hint="— vendor-confirmed wallet coverage from MassPay's Jan-2026 sheet. Facts only: NOTHING here opens sign-ups until the agreement is signed, Phase 1 is built, and the guardrails are verified (see the Guardrails tab)."
        >
          <table className="adm-table-light adm-w-full">
            <thead>
              <tr>
                <th style={{ width: "18%" }}>Market</th>
                <th>Currency</th>
                <th>MassPay FX</th>
                <th>Payout path</th>
                <th style={{ width: "42%" }}>Wallets</th>
              </tr>
            </thead>
            <tbody>
              {data.atlasPipeline!.map((p) => (
                <tr key={p.code}>
                  <td className="adm-bold adm-nowrap">{p.name}</td>
                  <td className="adm-semibold">{p.currency.toUpperCase()}</td>
                  <td className="adm-nowrap">
                    {p.fxPublished ? (
                      <span className="adm-bold">{p.fxPct!.toFixed(2)}%</span>
                    ) : (
                      <span className="muted-text">not published</span>
                    )}
                  </td>
                  <td className="adm-nowrap">
                    {p.cryptoOnly ? (
                      <span className="adm-bold adm-orange">🪙 crypto-only</span>
                    ) : (
                      <span className="adm-bold" style={{ color: "var(--adm-blue)" }}>📱 wallet + 🪙 crypto</span>
                    )}
                  </td>
                  <td className="muted-text adm-fs-78">{p.wallets.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Group>
      )}
    </div>
  );
}
